<script lang="ts">
  import Router, { push, link, location } from "svelte-spa-router";
  import { onMount } from "svelte";
  import Login from "./routes/Login.svelte";
  import ResetPassword from "./routes/ResetPassword.svelte";
  import SetNewPassword from "./routes/SetNewPassword.svelte";
  import VerifyEmail from "./routes/VerifyEmail.svelte";
  import Dashboard from "./routes/Dashboard.svelte";
  import Logs from "./routes/Logs.svelte";
  import DiagLinks from "./routes/DiagLinks.svelte";
  import Debug from "./routes/Debug.svelte";
  import Users from "./routes/Users.svelte";
  import UserDetail from "./routes/UserDetail.svelte";
  import UserEdit from "./routes/UserEdit.svelte";
  import Authorities from "./routes/Authorities.svelte";
  import AuthorityDetail from "./routes/AuthorityDetail.svelte";
  import Schools from "./routes/Schools.svelte";
  import SchoolDetail from "./routes/SchoolDetail.svelte";
  import RoomDetail from "./routes/RoomDetail.svelte";
  import Messages from "./routes/Messages.svelte";
  import DeviceConfig from "./routes/DeviceConfig.svelte";
  import Features from "./routes/Features.svelte";
  import LedColors from "./routes/LedColors.svelte";
  import Devices from "./routes/Devices.svelte";
  import DeviceDetail from "./routes/DeviceDetail.svelte";
  import Releases from "./routes/Releases.svelte";
  import Settings from "./routes/Settings.svelte";
  import Backup from "./routes/Backup.svelte";
  import ChangePassword from "./routes/ChangePassword.svelte";
  import { me, logout } from "./lib/api";
  import {
    reset,
    session,
    scopes,
    setLoading,
    setUser,
    canUseAdminApp,
    landingPathFor,
  } from "./lib/session.svelte";

  const routes = {
    "/": Login,
    "/reset": ResetPassword,
    "/reset/:token": SetNewPassword,
    "/verify/:token": VerifyEmail,
    "/dashboard": Dashboard,
    "/logs": Logs,
    "/diag-links": DiagLinks,
    "/debug": Debug,
    "/users": Users,
    "/users/:id": UserDetail,
    "/users/:id/edit": UserEdit,
    "/authorities": Authorities,
    "/authorities/:id": AuthorityDetail,
    "/schools": Schools,
    "/schools/:id": SchoolDetail,
    "/rooms/:id": RoomDetail,
    "/devices": Devices,
    "/devices/:label": DeviceDetail,
    "/releases": Releases,
    "/messages": Messages,
    "/device-config": DeviceConfig,
    "/features": Features,
    "/led-colors": LedColors,
    "/settings": Settings,
    "/backup": Backup,
    "*": Login,
  };

  onMount(async () => {
    setLoading(true);
    // Capture the route the user actually loaded, before any async work.
    const initialHash = window.location.hash;
    try {
      const res = await me();
      if (!canUseAdminApp(res.roles)) {
        setUser(null);
        push("/");
        return;
      }
      setUser({
        email: res.email,
        display_name: res.display_name,
        roles: res.roles,
        isAdmin: res.roles.some((r) => r.role === "admin"),
        mustChangePassword: res.must_change_password === true,
      });
      // Only send the user to their landing page when there's no real route
      // to restore (fresh visit / bare login). A reload on any real route
      // (including "/dashboard") must keep the user where they were.
      if (initialHash === "" || initialHash === "#/") {
        push(landingPathFor($scopes));
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  });

  async function doLogout() {
    try {
      await logout();
    } catch {
      /* ignore */
    }
    reset();
    push("/");
  }

  $: showNav = !!$session.user && $location !== "/" && $location !== "/reset";
  $: mustChange = !!$session.user && $session.user.mustChangePassword === true;
</script>

<main>
  {#if showNav}
    <nav data-testid="nav">
      <strong class="brand">wiederdabei Admin</strong>

      {#if $scopes.isAdmin}
        <a href="/dashboard" use:link data-testid="nav-dashboard">Dashboard</a>
      {/if}
      <a href="/devices" use:link data-testid="nav-devices">Devices</a>
      {#if $scopes.isAdmin}
        <a href="/authorities" use:link data-testid="nav-authorities">Authorities</a>
      {/if}
      <a href="/schools" use:link data-testid="nav-schools">Schools</a>
      {#if $scopes.isAdmin || $scopes.isAuthority || $scopes.isSchoolManager}
        <a href="/users" use:link data-testid="nav-users">User</a>
      {/if}
      {#if $scopes.isAdmin}
        <a href="/logs" use:link data-testid="nav-logs">Logs</a>
        <a href="/diag-links" use:link data-testid="nav-diag-links">Diagnose links</a>
        <a href="/debug" use:link data-testid="nav-debug">Debug</a>
        <a href="/releases" use:link data-testid="nav-releases">Releases</a>
        <a href="/settings" use:link data-testid="nav-settings">Settings</a>
        <a href="/backup" use:link data-testid="nav-backup">Backup</a>
      {/if}

      <div class="spacer"></div>
      <span class="who" data-testid="who">{$session.user?.email}</span>
      <button on:click={doLogout} data-testid="logout">Logout</button>
    </nav>
  {/if}
  {#if $session.loading}
    <div class="boot" data-testid="boot">Loading…</div>
  {/if}
  <div class="content">
    <!-- Gate routing on the initial auth check so protected route components
         don't mount (and bounce to login) before /auth/me resolves on boot. -->
    {#if !$session.loading}
      <Router {routes} />
    {/if}
  </div>
  {#if mustChange}
    <ChangePassword />
  {/if}
</main>

<style>
  main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  nav {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #111827;
    border-bottom: 1px solid #1f2937;
    padding: 0.6rem 1rem;
    flex-wrap: wrap;
  }
  .brand {
    color: #10b981;
    margin-right: 0.5rem;
  }
  nav a {
    color: #d1d5db;
    text-decoration: none;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }
  nav a:hover {
    background: #1f2937;
  }
  .spacer {
    flex: 1;
  }
  .who {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  nav button {
    padding: 0.3rem 0.6rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
  }
  .boot {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: rgba(15, 23, 42, 0.85);
    z-index: 10;
  }
  .content {
    flex: 1;
    padding: 1rem 1.25rem;
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
  }
</style>
